// @ts-check

/**
 * Puzzle completion percent for a History row or room snapshot.
 *
 * Completed rooms are 100%. Otherwise the figure is locked pieces over the grid,
 * floored and clamped to 0–100. A server-computed `progress_percent` wins when present
 * so History does not re-derive a number the Worker already published.
 *
 * @param {Record<string, any>|null|undefined} room
 * @returns {number}
 */
export function roomProgressPercent(room) {
  if (!room) return 0;
  if (room.is_completed || room.isCompleted) return 100;
  const published = Number(room.progress_percent);
  if (Number.isFinite(published)) {
    return clampPercent(published);
  }
  const total = Number(room.total_pieces) > 0
    ? Number(room.total_pieces)
    : (Number(room.cols) || 0) * (Number(room.rows) || 0);
  const locked = Number(room.pieces_locked ?? room.lockedPieceCount ?? 0);
  if (!(total > 0) || !Number.isFinite(locked)) return 0;
  return clampPercent(Math.floor((locked / total) * 100));
}

/**
 * Solved and total piece counts behind {@link roomProgressPercent}.
 *
 * @param {Record<string, any>|null|undefined} room
 * @returns {{ solved: number, total: number, percent: number }}
 */
export function roomProgressCounts(room) {
  const percent = roomProgressPercent(room);
  const totalRaw = Number(room?.total_pieces ?? room?.totalPieces);
  const grid = (Number(room?.cols) || 0) * (Number(room?.rows) || 0);
  const total = totalRaw > 0 ? totalRaw : (grid > 0 ? grid : 0);
  if (!room) return { solved: 0, total: 0, percent: 0 };
  if (room.is_completed || room.isCompleted) {
    return { solved: total, total, percent: 100 };
  }
  const lockedRaw = room.pieces_locked ?? room.piecesLocked ?? room.lockedPieceCount;
  const locked = Number(lockedRaw);
  const solved = total > 0
    ? (Number.isFinite(locked)
      ? Math.max(0, Math.min(total, Math.floor(locked)))
      : Math.max(0, Math.min(total, Math.round((percent / 100) * total))))
    : 0;
  return { solved, total, percent };
}

/**
 * Unix-ms wall clocks are ~1e12. `rooms.completed_time` stores elapsed seconds
 * for fastest-finish stats, so values below this threshold are durations, not dates.
 */
export const COMPLETED_TIME_TIMESTAMP_MIN_MS = 100000000000;

/**
 * True when `completed_time` is a Unix-ms stamp rather than puzzle duration in seconds.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isCompletedTimeTimestamp(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= COMPLETED_TIME_TIMESTAMP_MIN_MS;
}

/**
 * Wall-clock when a room was solved.
 *
 * `rooms.completed_at` is written once, when the puzzle finishes. A later visit
 * still updates `last_activity_at` and must not move this stamp. Older rows
 * that only stored a Unix-ms `completed_time` use that value. Duration
 * `completed_time` values fall back to `last_activity_at`.
 *
 * @param {Record<string, any>|null|undefined} room
 * @returns {number}
 */
export function communitySolvedStamp(room) {
  const frozen = Number(room?.completed_at ?? room?.completedAt);
  if (Number.isFinite(frozen) && frozen > 0) return frozen;
  const completedTime = room?.completed_time ?? room?.completedTime;
  if (isCompletedTimeTimestamp(completedTime)) return Number(completedTime);
  return Number(room?.last_activity_at ?? room?.lastActivityAt) || 0;
}

/**
 * Wall-clock used to decide whether a completed room still belongs in History.
 *
 * @param {Record<string, any>|null|undefined} room
 * @returns {number}
 */
export function historyCompletedStamp(room) {
  return communitySolvedStamp(room);
}

/**
 * History keeps unfinished rooms, and completed rooms inside the retention window.
 *
 * @param {Record<string, any>|null|undefined} room
 * @param {number} now
 * @param {number} windowMs
 * @returns {boolean}
 */
export function isRecentHistoryRoom(room, now, windowMs) {
  if (!room) return false;
  if (!(room.is_completed || room.isCompleted)) return true;
  const stamp = historyCompletedStamp(room);
  if (!stamp) return true;
  return (now - stamp) <= windowMs;
}

/**
 * Doughnut chart for a room's solved share. The tooltip is solved/total and the percent.
 *
 * @param {Record<string, any>|null|undefined} room
 * @param {(value: unknown) => string} [escapeHtml]
 * @returns {string}
 */
export function progressDoughnutHtml(room, escapeHtml) {
  const esc = typeof escapeHtml === 'function' ? escapeHtml : (value) => String(value ?? '');
  const { solved, total, percent } = roomProgressCounts(room);
  const label = solved + '/' + total + ' pieces · ' + percent + '%';
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const filled = (percent / 100) * circumference;
  const color = percent >= 100 ? '#4ade80' : '#38bdf8';
  return (
    '<span class="progress-doughnut" title="' + esc(label) + '" aria-label="' + esc(label) + '">' +
      '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="' + radius + '" fill="none" stroke="#334155" stroke-width="3"></circle>' +
        '<circle cx="12" cy="12" r="' + radius + '" fill="none" stroke="' + color + '" stroke-width="3" ' +
          'stroke-dasharray="' + filled.toFixed(2) + ' ' + (circumference - filled).toFixed(2) + '" ' +
          'stroke-linecap="round" transform="rotate(-90 12 12)"></circle>' +
      '</svg>' +
    '</span>'
  );
}

/**
 * @param {number} value
 * @returns {number}
 */
function clampPercent(value) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.floor(value)));
}

/**
 * Formats a duration in seconds into human-readable compact format (e.g. "45s", "5m 20s", "1h 12m").
 *
 * @param {number|null|undefined} seconds
 * @returns {string}
 */
export function formatDuration(seconds) {
  const sec = Math.max(0, Math.floor(Number(seconds) || 0));
  if (sec === 0) return '0s';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}
